import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MultiselectDropdownComponent } from '@niceltd/cxone-components/multiselect-dropdown';
import { Subscription } from 'rxjs';
import { GroupSelectorDTO } from 'src/AngularJS/Reusables/group-selector.models';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { BaseGroupType } from 'src/app/reusables/models/group-type.model';
import { CXOneMultiSelectChanged, MultiSelectArrays } from 'src/app/reusables/multi-select/multi-select.models';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { AttributeSelectorModel, AttributeType, SelectedCategoryModel } from 'src/shared/models/attribute-selector.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';

@Component({
    selector: 'cv-repeated-selectors',
    templateUrl: './repeated-selectors.component.html',
    styleUrls: ['./repeated-selectors.component.scss']
})
export class RepeatedSelectorsComponent implements OnInit, OnDestroy {

    @Input() filledCategories: Map<string, MultiSelectArrays> = new Map();
    @Input() attributesLoading: boolean;
    @Input() statedAttributes: AttributeSelectorModel;
    @Input() pageName: string;
    @Input() categoryNameIdMap = new Map<string, string>();
    @Input() settingsManager?: SlideoutSettingsManager;
    @Input() viewByLimitsFilters: number = 0; // viewByLimitsFilters value is coming from cv-cxone-attribute-selector component, the variable is viewByLimitsFilters and its value is what is coming from the db through SettingViewByLimitsFilters.

    @ViewChildren(MultiselectDropdownComponent) multiSelectComponents: QueryList<MultiselectDropdownComponent>;

    /** used to keep track of which selector was last changed in order to reset all attributes to selected in the case that user deselects all attributes */
    lastAttributeSelectorChanged: string = null;
    private _openPromise: Promise<void> = null;
    private _resolveOpenPromise: () => void = null;
    private _beforeApplySubscription: Subscription;
    private _savingPromise: Promise<ApiResponse<GroupSelectorDTO>> = null;


    constructor(
        public service: AttributeSelectorService,
    ) { }

    async ngOnInit() {
        await this.initialize();
    }

    async initialize() {
        this._beforeApplySubscription = this.settingsManager.beforeSettingsApplied.subscribe(() => {
            if (this._savingPromise !== null) {
                this.settingsManager.waitBeforeApplying.push(this._savingPromise);
            } else if (this._openPromise !== null) { // selector is open and will likely save after closing
                this.settingsManager.waitBeforeApplying.push(this._openPromise);
                this.multiSelectComponents?.forEach(c => { if (c.isDropdownOpen) c.toggleDropdown(); });
            }
        });
    }

    ngOnDestroy() {
        this._beforeApplySubscription.unsubscribe();
    }

    checkIfChanges() {
        // send over a name to id map from cxone attribute selector and check only that categories selections
        const lastSavedIds = [];
        this.statedAttributes.SelectedCategories.forEach(cat => {
            if (cat.SelectedCategoryID.toString() === this.categoryNameIdMap.get(this.lastAttributeSelectorChanged)) {
                lastSavedIds.push(...cat.SelectedAttributeIDs.sort());
            }
        });
        const lastSavedIdsString = lastSavedIds.filter(id => id !== -1).filter(id => id !== 0).toString();

        const currentSelected = this.filledCategories.get(this.lastAttributeSelectorChanged).selection.map(cat => cat.id).sort().toString();
        if (lastSavedIdsString !== currentSelected) {
            return true;
        }
        else {
            return false;
        }
    }

    onAttributeSelectorOpen(changedSelectorKey: string) {
        this.lastAttributeSelectorChanged = changedSelectorKey;
        this._openPromise = new Promise(resolve => this._resolveOpenPromise = resolve);
    }

    onAttributeSelectorClose() {
        this.reselectAllIfNoneSelected();
        if (this.checkIfChanges()) {
            try {
                this.save();
            }
            finally {
                this.resolveOpenPromise();
            }
        }
        else {
            this.resolveOpenPromise();
        }
    }

    resolveOpenPromise() {
        this._resolveOpenPromise?.();
        this._resolveOpenPromise = null;
        this._openPromise = null;
    }

    async save() {
        const categories = this.updateSelectedInCategory();
        let viewByID = null;
        if (this.settingsManager?.selectedToggle === BaseGroupType.DataAttribute) {
            viewByID = this.settingsManager?.selectedLevel;
        }
        const model: AttributeSelectorModel = {Type: AttributeType.Data, SelectedCategories: categories, GroupedCategoryID: viewByID};
        await this.service.saveSelectorModel(this.pageName, model);
    }

    updateSelectedInCategory(): SelectedCategoryModel[] {
        const categoriesModel: SelectedCategoryModel[] = [];
        for (const [key, value] of this.filledCategories) {
            const catID = +this.categoryNameIdMap.get(key);
            const selectedIDs = value.selection.map(option => +option.id);
            categoriesModel.push({SelectedCategoryID: catID, SelectedAttributeIDs: selectedIDs} as SelectedCategoryModel);
        }
        return categoriesModel;
    }

    reselectAllIfNoneSelected() {
        if (this.lastAttributeSelectorChanged == null)
            return;
        const attributeSelector = this.filledCategories.get(this.lastAttributeSelectorChanged);
        const numSelected = attributeSelector.options.filter(option => option.selected).length;
        if (numSelected === 0) {
            attributeSelector.options.forEach(option => {
                option.selected = true;
            });
            attributeSelector.selection = attributeSelector.options.filter(option => option.selected);
            if (this.settingsManager?.selectedLevel?.toString() === this.categoryNameIdMap.get(this.lastAttributeSelectorChanged) && this.settingsManager?.selectedToggle === BaseGroupType.DataAttribute && this.viewByLimitsFilters > 0) {
                attributeSelector.selection.forEach(attribute => {
                    attribute.selected = false;
                });
                attributeSelector.selection.slice(0, this.viewByLimitsFilters).forEach(attribute => {
                    attribute.selected = true;
                });

                attributeSelector.selection = attributeSelector.selection.splice(0, this.viewByLimitsFilters);
            }
        }
    }

    onAttributeSelectionChanged(change: CXOneMultiSelectChanged) {
        this.filledCategories.get(this.lastAttributeSelectorChanged).selection = change.selected.filter(x => x.selected);
    }

    useMaxSelectionLimit(changedSelectorKey: string) {
        if (this.settingsManager?.selectedToggle === BaseGroupType.DataAttribute && this.categoryNameIdMap.get(changedSelectorKey) === this.settingsManager?.selectedLevel?.toString() && this.viewByLimitsFilters > 0) {
            return true;
        } else {
            return false;
        }
    }
}
